/**
 * @module services/convert
 * @description 统一的配置转化层 convert() — 将全局 BASE+Draft 转化为接口提交参数
 *
 * 数据流（S1-S6）：
 *   S1: 从 Config A 收集 chartType 下所有 configItemId
 *   S2: 从 Config B 查 uploadKeys → 去重得字段名列表
 *   S3: merged = { ...base, ...draft } → pick(字段名列表)
 *   S4: filterFormValues(formDelKey, picked) → 过滤不可见字段
 *   S5: applyTypeTransform(chartType, filtered) → 图表特有转换
 *   S6: 从 Config C 取存储键 → 构造 { customizeSetting: { [key]: values } }
 *
 * 关键：
 * - 配置项上传字段格式绝对不允许更改（与现有接口保持完全一致）
 * - convert() 是纯函数，不依赖 Context/Store
 * - formDelKey 由 Panel 层计算后传入：key=字段名, value=true=该字段因不可见需过滤
 *
 * 性能（调用方须知）：
 * - 仅用于提交/重算等点击路径，禁止在组件 render 或高频 useMemo 中调用。
 * - 复杂度 O(配置项 uploadKeys 数量)，与查询结果行数无关；空 base 仍会走 Config 表，但为亚毫秒级。
 * - 调用方勿用 Object.keys(base) 短路 convert；无面板图表类型输出空 customizeSetting 为约定行为。
 *
 * 兼容处理（C2, C5, C10 + DataLabel 关闭剥离）：
 * - C2: customMetricSort ↔ chartSorts 双写
 * - C5: dualYAxisEnabled 显式写入 + 关闭时删除轴配置
 * - C10: xAxisValues 特殊值清洗
 * - DataLabel：`showDataLabel === false` 时不上传三字段（读侧缺省视为关闭）
 * - C1（showValues 双写）已移除：Confirm 只上传新字段，读侧由 normalizeDataLabelConfig 兼容
 *
 * @see 渲染方案 §六 — 转化层架构
 * @see PRD 附录-配置项数据结构向后兼容分析 — C2, C5, C10
 */

import { ChartType, ConfigChartType, type TChartType } from '@dbl/core/chart';
import PANEL_LAYOUT_BY_CHART from '../manifest/panelLayout';
import CONFIG_ITEM_UPLOAD_KEYS from '../manifest/upload-keys';
import type { TChartSettingsConfigLayer } from '../types';
import { applyTypeTransform } from './type-transformers';
import { logConvertFailure } from './convertFailureLog';
import { normalizeCrosstabConfig } from '../utils/crosstab';

/**
 * 图表类型特有字段排除表
 *
 * 某些字段在特定图表类型中无意义（如饼图不支持 X 轴标签方向），
 * 需在转化层 S2+ 步骤强制排除，确保即使 formDelKey 为空也不会输出。
 *
 * key: TChartType, value: 需排除的字段名数组
 */
const CHART_TYPE_EXCLUDE_FIELDS: Partial<Record<TChartType, string[]>> = {
  [ChartType.Pie]: ['x_label'],
};

/** IFieldVisibility: key=字段名, value=true=不可见需过滤 */
type IFieldVisibility = Record<string, boolean>;

/** 转化函数的输入参数 */
interface ConvertInput {
  /** 当前图表类型 */
  chartType: TChartType;
  /** 已确认的全局 BASE */
  base: TChartSettingsConfigLayer;
  /** 编辑中的 Draft（优先级高于 base） */
  draft: TChartSettingsConfigLayer;
  /** 未确认修改标记 — 用于识别 draft 中已删除的字段 */
  hasDraft?: Record<string, boolean>;
  /** 不可见字段标记表 — 由 Panel 层计算 */
  formDelKey: IFieldVisibility;
}

/** 转化函数的输出 */
interface ConvertOutput {
  customizeSetting: Record<string, Record<string, unknown>>;
}

/** 异常兜底返回值（与「无 storageKey 的图表类型」共用同一引用，避免每次 catch 分配新对象） */
const EMPTY_CUSTOMIZE_SETTING: ConvertOutput = {
  customizeSetting: {},
};

/**
 * 将可能为 null / 非纯对象的入参归一为 Record。
 *
 * 为何不用 lodash-es（isPlainObject 等）：
 * - 仅 3 行判断，无复用场景，原生实现更直观且无额外 import。
 * - 需同时排除数组；lodash 的 isObject 会把数组算进去，易误用。
 *
 * 性能：O(1)，仅在 convert 入口对 4 个入参各调用一次。
 */
function normalizeRecord(
  value: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value;
}

/**
 * S1: 从 Config A 收集 chartType 下所有 Tab 内所有 group 的 ID
 *
 * @param chartType - 当前图表类型
 * @returns 去重后的配置项 ID 数组
 */
function collectConfigItemIds(chartType: TChartType): string[] {
  const tabs = PANEL_LAYOUT_BY_CHART[chartType];
  if (!tabs || tabs.length === 0) return [];

  const ids = new Set<string>();
  for (const tab of tabs) {
    if (!tab?.groups) continue;
    for (const groupId of tab.groups) {
      if (groupId) {
        ids.add(groupId);
      }
    }
  }
  return Array.from(ids);
}

/**
 * S2: 从 Config B 查每个配置项 ID 对应的上传字段名，去重
 *
 * @param itemIds - 配置项 ID 列表
 * @returns 去重后的上传字段名数组
 */
function resolveUploadKeys(itemIds: string[]): string[] {
  const keys = new Set<string>();
  for (const itemId of itemIds) {
    const uploadKeys = CONFIG_ITEM_UPLOAD_KEYS[itemId];
    if (!uploadKeys) continue;
    for (const k of uploadKeys) {
      keys.add(k);
    }
  }
  return Array.from(keys);
}

/**
 * S3: 从 merged(base, draft) 中挑选指定字段
 *
 * Draft 优先级高于 Base（用户未确认的修改覆盖已确认的值）
 *
 * @param base - 全局 BASE
 * @param draft - 全局 Draft
 * @param keys - 需要挑选的字段名数组
 * @returns 挑选出的字段值
 */
function pickFromMerged(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  keys: string[],
  hasDraft: Record<string, boolean> = {},
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    // draft 中显式删除（hasDraft 有标记但 key 不在 draft）→ 不上传
    if (hasDraft[key] && !(key in draft)) {
      continue;
    }
    if (key in draft) {
      result[key] = draft[key];
    } else if (key in base) {
      result[key] = base[key];
    }
  }
  return result;
}

/**
 * S4: filterFormValues — 过滤因 QB 条件不满足而不可见的配置字段
 *
 * @param formDelKey - key=字段名, value=true=不可见需过滤
 * @param picked - S3 pick 出的值
 * @returns 过滤后的值
 */
function filterFormValues(
  formDelKey: IFieldVisibility,
  picked: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(picked)) {
    if (!formDelKey[key]) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * DataLabel: showDataLabel===false 时剥离整组字段
 * C2: customMetricSort ↔ chartSorts 双写（sortBy=datasetField 时不双写，仅保留 chartSorts）
 * C5: dualYAxisEnabled 显式写入 + 关闭时 delete 轴配置
 * C10: xAxisValues 特殊值清洗
 *
 * @param values - S5 转换后的值（会被原地修改）
 */
function applyCompatibilityWrites(values: Record<string, unknown>): void {
  // 数据标签关闭：整组不上传（下次加载缺省视为关，由 normalizeDataLabelConfig 兜底）
  if (values.showDataLabel === false) {
    delete values.showDataLabel;
    delete values.displayForm;
    delete values.labelContent;
  }

  // C2: chartSorts ↔ customMetricSort 双写（读取优先 customMetricSort）
  // 字段排序（datasetField）为新协议：只上传 chartSorts，禁止双写 customMetricSort
  if (values.sortBy === 'datasetField') {
    if (values.chartSorts) {
      // eslint-disable-next-line no-param-reassign
      delete values.customMetricSort;
    }
  } else {
    const sorts = values.customMetricSort || values.chartSorts;
    if (sorts) {
      // eslint-disable-next-line no-param-reassign
      values.chartSorts = sorts;
      // eslint-disable-next-line no-param-reassign
      values.customMetricSort = sorts;
    }
  }

  // C5: dualYAxisEnabled 显式写入
  if ('dualYAxisEnabled' in values) {
    // eslint-disable-next-line no-param-reassign
    values.dualYAxisEnabled = !!values.dualYAxisEnabled;
    // 关闭时删除轴配置，避免后端误读
    if (!values.dualYAxisEnabled) {
      delete values.primaryYAxis;
      delete values.secondYAxis;
    }
  }

  // C3: sortBy 前端存量值 → 上传协议（与 ChartsDrop sortByKey 一致）
  const SORT_BY_UPLOAD_MAP: Record<string, string> = {
    defaultSort: 'default',
    customMetricSort: 'metric',
    manual: 'x-axis',
  };
  const rawSortBy = values.sortBy as string | undefined;
  if (rawSortBy && SORT_BY_UPLOAD_MAP[rawSortBy]) {
    // eslint-disable-next-line no-param-reassign
    values.sortBy = SORT_BY_UPLOAD_MAP[rawSortBy];
  }

  // C9: crosstab 上传结构必须为 { table_code, column }[]（兼容存量 string[]）
  if (values.crosstab && typeof values.crosstab === 'object') {
    const normalized = normalizeCrosstabConfig(values.crosstab, [], []);
    if (normalized) {
      // eslint-disable-next-line no-param-reassign
      values.crosstab = normalized;
    }
  }

  // C10: xAxisValues 归一化为 string[] 并做特殊值清洗
  if (Array.isArray(values.xAxisValues)) {
    // eslint-disable-next-line no-param-reassign
    values.xAxisValues = (values.xAxisValues as unknown[]).map((item) => {
      if (typeof item === 'string' || typeof item === 'number') {
        return String(item);
      }
      if (item && typeof item === 'object') {
        const row = item as { id?: string | number; value?: string | number };
        return String(row.value ?? row.id ?? '');
      }
      return '';
    }).map((v) => {
      if (v === 'custom_undefined') return '';
      if (v === '[NULL]') return 'null';
      return v;
    });
  }
}

/**
 * 统一的转化函数 — 计算 / 保存 / 切图等请求共用。
 *
 * 调用约定（性能与语义）：
 * - 仅在用户点击「计算」「保存」「切图」等低频路径调用，不在 render / useMemo 热路径使用。
 * - 调用方无需 `Object.keys(base).length` 预判：有面板的图表类型在空 base 时仍会得到 `{ [storageKey]: {} }`；
 *   无面板类型（Config A 为空）直接返回 `{ customizeSetting: {} }`。
 * - 工作量与「配置项 uploadKeys 数量」成正比（通常几十项），与数据集大小无关；
 *   base 为空时仍会遍历 Config A/B，但耗时在亚毫秒级，可忽略。
 *
 * 健壮性：
 * - 入参在函数入口 normalize 一次（避免 `key in null` 抛错）。
 * - try/catch 仅包裹整段流水线；正常路径无额外分支，catch 几乎不触发。
 *
 * @param input - { chartType, base, draft, formDelKey }
 * @returns { customizeSetting: { [storageKey]: values } }
 *
 * @example
 * const payload = convert({
 *   chartType: ChartType.Line,
 *   base: { yAxisName: '收入' },
 *   draft: { yAxisName: '新名称' },
 *   formDelKey: {},
 * });
 * // → { customizeSetting: { lineChart: { yAxisName: '新名称', ... } } }
 */
export function convert(input: ConvertInput): ConvertOutput {
  try {
    const chartType = input.chartType;
    if (!chartType) {
      return EMPTY_CUSTOMIZE_SETTING;
    }

    // 入口一次性归一化（勿在子步骤重复调用）
    const base = normalizeRecord(input.base);
    const draft = normalizeRecord(input.draft);
    const hasDraft = normalizeRecord(input.hasDraft) as Record<string, boolean>;
    const formDelKey = normalizeRecord(input.formDelKey) as IFieldVisibility;

    // S1: 收集当前图表类型的所有配置项 ID
    const itemIds = collectConfigItemIds(chartType);

    // 无右侧面板（BigNumber / Trend / DimNumberTrend 等）→ 无可上传配置项，不构造 storageKey
    if (itemIds.length === 0) {
      return EMPTY_CUSTOMIZE_SETTING;
    }

    // S2: 查上传字段名
    const uploadKeys = resolveUploadKeys(itemIds);

    // S2+: 图表类型特有字段强制排除（确保 formDelKey 为空时也不输出）
    const excludeSet = new Set([
      ...(CHART_TYPE_EXCLUDE_FIELDS[chartType] || []),
    ]);
    const effectiveKeys = uploadKeys.filter((k) => !excludeSet.has(k));

    // S3: 从全局 BASE + Draft 挑选字段（无 column_sort 时 pick 自然跳过）
    const picked = pickFromMerged(base, draft, effectiveKeys, hasDraft);

    // S4: 过滤不可见配置
    const filtered = filterFormValues(formDelKey, picked);

    // S5: 图表类型特有转换
    const transformed = applyTypeTransform(chartType, filtered);

    // 兼容写入：确保上传字段格式与现有接口完全一致
    applyCompatibilityWrites(transformed);

    // S6: 构造返回结果
    const storageKey = ConfigChartType[chartType];
    if (!storageKey) {
      return EMPTY_CUSTOMIZE_SETTING;
    }

    return {
      customizeSetting: {
        [storageKey]: transformed,
      },
    };
  } catch (error) {
    logConvertFailure(input?.chartType, error);
    return EMPTY_CUSTOMIZE_SETTING;
  }
}

